import { memo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Bookmark, MapPin, Navigation, Star } from "lucide-react";
import { useSaved } from "../hooks/useSaved";
import { useEventCover } from "../hooks/useEventCover";
import { LikeButton } from "./LikeButton";
import { formatCardDate } from "../lib/dates";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export const EventCard = memo(({ event, variant = "grid", index = 0, distanceLabel = null, featured = false, preview = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSaved, toggle } = useSaved();
  const saved = isSaved(event.id);
  const { src: imgSrc, onError: onImgError } = useEventCover(event);
  // NEW badge: shown automatically on any page whose event carries `created_at`
  // (all fetches already select it) when the listing was created within the last
  // 7 days. Sits alongside category/LIVE — never replaces distance/LIVE/Paid-Free.
  const isNew =
    event.created_at &&
    Date.now() - new Date(event.created_at).getTime() < SEVEN_DAYS_MS;

  const widthClass =
    variant === "carousel"
      ? "w-[80vw] max-w-[320px] shrink-0 snap-start"
      : variant === "compact"
      ? "w-[46vw] max-w-[200px] shrink-0 snap-start md:max-lg:w-[150px]"
      : "w-full";

  const go = () => {
    if (!preview) navigate(`/event/${event.id}`, { state: { backgroundLocation: location } });
  };

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`event-card-${event.id}`}
      onClick={go}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") go();
      }}
      className={`group relative block text-left ${preview ? "cursor-default" : "cursor-pointer"} ${widthClass} rr-fade-up`}
      style={{ animationDelay: `${Math.min(index, 7) * 70}ms`, WebkitTapHighlightColor: "transparent" }}
    >
      <div
        className={`relative aspect-[4/5] w-full overflow-hidden rounded-3xl ${variant === "grid" ? "md:aspect-[16/10]" : ""} bg-[#121212] ${
          featured
            ? "border border-[#ff2a00]/60"
            : "border border-white/10"
        }`}
      >
        <img
          src={imgSrc}
          alt={event.name}
          loading="lazy"
          onError={onImgError}
          className="h-full w-full object-cover transition-transform duration-700 ease-out [@media(hover:hover)]:group-hover:scale-105"
        />
        <div className="absolute inset-x-0 top-0 flex items-start justify-between p-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {featured && (
              <span className="flex items-center gap-1 whitespace-nowrap rounded-full bg-[#ff2a00] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow-md shadow-[#ff2a00]/30">
                <Star className="h-2.5 w-2.5 fill-white" />
                Featured
              </span>
            )}
            {event.live && (
              <span
                data-testid={`live-badge-${event.id}`}
                className="flex items-center gap-1 whitespace-nowrap rounded-full bg-[#ffb000] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-black shadow-md shadow-[#ffb000]/40"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-black animate-pulse" />
                Live now
              </span>
            )}
            {!featured && !event.live && (
              <span className="whitespace-nowrap rounded-full bg-[#050505]/85 px-2.5 py-0.5 text-[10px] font-semibold tracking-wide text-white">
                {event.category}
              </span>
            )}
            {isNew && (
              <span
                data-testid={`new-badge-${event.id}`}
                className="whitespace-nowrap rounded-full border border-[#ff2a00]/50 bg-[#050505]/70 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#ff2a00]"
              >
                New
              </span>
            )}
          </div>
          <button
            data-testid={`save-toggle-${event.id}`}
            onClick={(e) => {
              e.stopPropagation();
              if (!preview) toggle(event.id);
            }}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-[#050505]/85 transition-transform duration-200 active:scale-90"
            aria-label="Save event"
          >
            <Bookmark
              className={`h-[18px] w-[18px] transition-colors ${
                saved ? "fill-[#ff2a00] text-[#ff2a00]" : "text-white"
              }`}
            />
          </button>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/80 to-transparent" />
        {distanceLabel && (
          <div
            data-testid={`distance-${event.id}`}
            className="absolute bottom-3 left-3 flex items-center gap-1 rounded-full bg-[#ff2a00] px-2.5 py-1 text-[11px] font-bold text-white"
          >
            <Navigation className="h-3 w-3 fill-white" />
            {distanceLabel}
          </div>
        )}
        {!preview && <LikeButton eventId={event.id} variant="card" />}
      </div>

      <div className="px-1 pt-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#ff2a00]">
            {formatCardDate(event)}
          </p>
          <span
            data-testid={`entry-pill-${event.id}`}
            className={`shrink-0 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
              event.is_free
                ? "border-[#ff2a00]/40 bg-[#ff2a00]/10 text-[#ff2a00]"
                : "border-white/15 bg-white/5 text-white/70"
            }`}
          >
            {event.is_free ? "Free" : "Paid"}
          </span>
        </div>
        <h3 className="font-display mt-1 text-lg font-bold leading-tight tracking-tight text-white md:line-clamp-2 md:text-base">
          {event.name}
        </h3>
        <p className="mt-1 flex items-center gap-1 text-sm text-white/50">
          <MapPin className="h-3.5 w-3.5" /> {event.location}
        </p>
      </div>
    </div>
  );
});
